import { Permission, ProviderAccessToken } from '../../admin/admin.models';

export type UserPermissions = string[];

export enum CompanyDataType {
  FM = 'FM',
  IN = 'IN',
  SI = 'SI',
}

export type CompanyDTO = {
  company_name: string;
  data_type: CompanyDataType;
};

export type CompanySubscriptionState =
  | 'pending'
  | 'trialing'
  | 'active'
  | 'canceling'
  | 'past_due'
  | 'expired'
  | 'trial_end';

export type CompanySubscriptionType = 'consumer' | 'commercial';
export type CompanySubscriptionTier = 'tracked' | 'untracked';

/**
 * Subscription states.
 */
export enum CompanyState {
  ACTIVE = 'ACTIVE', // Active
  TERMINATING = 'TERMINATING',
  TERMINATED = 'TERMINATED',
}

export type Company = CompanyDTO & {
  id: number;
  status: CompanyState;
  terminate_after: string | null;
  owners: { first_name: string; last_name: string; id: number }[];
  vehicles: string[];
  country: string;
  city: string;
  date_created: Date;
  subscription_exists: boolean;
  subscription_state: CompanySubscriptionState | null;
  subscription_is_free: boolean | null;
  subscription_type: CompanySubscriptionType | null;
  subscription_tiers: CompanySubscriptionTier[];
};

/**
 * Display names for the Company states.
 */
export const COMPANY_STATE_DISPLAY: { [key in CompanyState]: string } = {
  [CompanyState.ACTIVE]: 'Active',
  [CompanyState.TERMINATING]: 'Scheduled Deletion',
  [CompanyState.TERMINATED]: 'Deleted',
};

/**
 * Icon for the COMPANY states.
 */
export const COMPANY_STATE_ICON: {
  [key in CompanyState]: 'checkmark' | 'block' | 'error_outline';
} = {
  [CompanyState.ACTIVE]: 'checkmark',
  [CompanyState.TERMINATING]: 'error_outline',
  [CompanyState.TERMINATED]: 'error_outline',
};

export const enum UserPolicyStatus {
  CHECKED = 'checked',
  OUTDATED = 'outdated',
  UNCHECKED = 'unchecked',
}

export type UserProfileDTO = {
  email: string;
  first_name: string | null;
  last_name: string | null;
  groups?: number[];
};
export type UserProfile = UserProfileDTO & {
  id: number;
  date_joined: Date | null;
  last_login: Date | null;
  phone_number: string | null;
  groups: number[];
  permissions: UserPermissions;
  emailVerified: boolean;
  subscribed_to_emails: boolean;
  accepted_terms_and_conditions: UserPolicyStatus;
  accepted_privacy_policy: UserPolicyStatus;
  accepted_terms_of_service: UserPolicyStatus;
  country: string | null;
  postal_code: string | null;
  birth_year: number | null;
  gender: string | null;
  is_staff: boolean;
  is_individual: boolean;
  sso_provider: string;
  is_superuser: boolean;
};
export type UserProfileForm = {
  email: string;
  password: string;
};

export type UserPreferencesDTO = {
  distance_unit: string;
  appearance?: string;
  language: string;
  timezone: string;
};

export type SessionResponse = {
  isAuthenticated: boolean;
  user?: UserProfile;
  preferences?: UserPreferencesDTO;
};

export type FindUserResponse = {
  exists: boolean;
  verified: boolean;
};

export type SignInResponse = {
  user: UserProfile;
  detail: string;
  expireIn: number;
  provider_token?: ProviderAccessToken;
};

export type ForgotPasswordResponse = {
  success: boolean;
  message: string;
};

export type ResetPasswordResponse = {
  message: string;
  success: boolean;
};

export type UserGroupDTO = {
  id?: number;
  name: string;
  description?: string;
  permissions: number[];
  color: string;
};
export type UserGroup = Omit<UserGroupDTO, 'id' | 'permissions'> & {
  id: number;
  can_modify: boolean;
  permissions: Permission[];
};

export type UserCanDeleteResponse = {
  can_delete: boolean;
  ownerships?: string[];
};

export type DemoCodeDTO = {
  code: string;
  days: number;
  valid_from: string;
  valid_until: string | null;
  uses_left: number;
};

export type IpData = {
  ip: string | null;
  country: string | null;
};

export type Tax = {
  name: string;
  rate: number;
  display_name: string;
  tax_type: string;
};

export type RewardPlatform = 'stripe' | 'ios' | 'playstore';
export type RewardStatus = 'pending' | 'issued' | 'redeemed' | 'expired';

export type ReferralCodeResponse = {
  referral_code: string;
};

export type PromoDTO = {
  id?: number;
  platform?: RewardPlatform;
  external_id?: string;
  code_display?: string;
  issued_at?: string | null;
  expires_at?: string | null;
};

export type RewardDTO = {
  id: number;
  platform: RewardPlatform;
  status: RewardStatus;
  issued_at: string | null;
  redeemed_at: string | null;
  expires_at: string | null;
  promo?: PromoDTO | null;
  attribution?: {
    referee?: {
      id: number;
      email?: string;
      first_name?: string;
      last_name?: string;
    };
  } | null;
};
